import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import type { ProductFeedUpdateRequestSchema } from "@polifonia/interlude/models";
import { theme } from "@polifonia/theme";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DownloadTemplates } from "@/pages/applications/pages/productFeeds/components/DownloadTemplates";
import { FeedCheckSummary } from "@/pages/applications/pages/productFeeds/components/FeedCheckSummary";
import { FeedNameField } from "@/pages/applications/pages/productFeeds/components/FeedNameField";
import { FileDropZone } from "@/pages/applications/pages/productFeeds/components/FileDropZone";
import { useFileUpload } from "@/pages/applications/pages/productFeeds/hooks/useFileUpload";

type ProductFeed = InterludeTypes["ProductFeed"];

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
`;

const cssFormContainer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  width: 100%;
`;

const cssButtonContainer = css({
  display: "flex",
  gap: "12px",
  marginTop: "8px",
});

const cssSyncingBadgeWrapper = css`
  width: fit-content;
`;

interface FileUploadFormProps {
  feed: ProductFeed;
}

export const FileUploadForm: React.FC<FileUploadFormProps> =
  function FileUploadForm({ feed }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [feedName, setFeedName] = useState(feed.name);

    const fileUpload = useFileUpload();

    // Use filename from feed directly
    const existingFileName = feed.filename ?? undefined;

    const [isExistingFileRemoved, setIsExistingFileRemoved] = useState(false);

    const existingFile = useMemo(() => {
      if (
        isExistingFileRemoved ||
        !existingFileName ||
        fileUpload.selectedFile
      ) {
        return null;
      }
      // Create a virtual File object with the existing filename
      // This will be displayed as if the user has selected this file
      return new File([], existingFileName, {
        type: "application/octet-stream",
      });
    }, [existingFileName, fileUpload.selectedFile, isExistingFileRemoved]);

    // Use selectedFile if user selected a new file, otherwise use existingFile
    const displayFile = fileUpload.selectedFile || existingFile;

    const updateFeedMutation = interlude.productFeed.useUpdateFeed(
      {
        params: {
          feedId: feed.id,
        },
      },
      {
        onError: (error) => {
          if (error instanceof AxiosError && error.response?.status === 409) {
            enqueueSnackbar(
              t("productFeed.detailPage.error.syncing", {
                feedName: feed.name,
              }),
              {
                variant: "error",
                autoHideDuration: 5000,
              },
            );
          } else {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            enqueueSnackbar(
              `${t("productFeed.fileUpload.error.updateFailed")}: ${errorMessage}`,
              {
                variant: "error",
              },
            );
          }
        },
      },
    );

    const isSubmitting = fileUpload.isUploading || updateFeedMutation.isPending;

    const handleRemoveExistingFile = useHandler(() => {
      setIsExistingFileRemoved(true);
      fileUpload.handleRemoveFile();
    });

    const handleSubmit = useHandler(async () => {
      if (isSyncing) {
        enqueueSnackbar(
          t("productFeed.detailPage.error.syncing", {
            feedName: feed.name,
          }),
          {
            variant: "error",
            autoHideDuration: 5000,
          },
        );
        return;
      }

      if (!feedName.trim()) {
        enqueueSnackbar(t("productFeed.fileUpload.error.nameRequired"), {
          variant: "error",
        });
        return;
      }

      // Check if there's a file (either newly selected or existing)
      if (!fileUpload.selectedFile && !existingFile) {
        enqueueSnackbar(t("productFeed.fileUpload.error.fileRequired"), {
          variant: "error",
        });
        return;
      }

      let gcsUri = feed.feedUrl;

      // If a new file is selected, upload it first
      if (fileUpload.selectedFile) {
        try {
          gcsUri = await fileUpload.uploadFile(fileUpload.selectedFile);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          enqueueSnackbar(
            `${t("productFeed.fileUpload.error.updateFailed")}: ${errorMessage}`,
            {
              variant: "error",
            },
          );
          return;
        }
      }
      // If no new file is selected, keep the existing feedUrl

      // Let onError callback handle mutation errors
      updateFeedMutation.mutate({
        name: feedName.trim(),
        feedUrl: gcsUri,
        filename: fileUpload.selectedFile
          ? fileUpload.selectedFile.name
          : (feed.filename ?? null),
      } satisfies z.infer<typeof ProductFeedUpdateRequestSchema>);
    });

    const isSyncing = feed.status === "syncing";

    return (
      <Stack css={cssFormContainer}>
        <Typography variant="body" css={cssDescription}>
          {t("productFeed.fileUpload.description")}
        </Typography>

        <FeedNameField
          value={feedName}
          onChange={setFeedName}
          disabled={isSubmitting || isSyncing}
          translationPrefix="fileUpload"
        />

        <DownloadTemplates />

        <FileDropZone
          selectedFile={displayFile}
          disabled={isSubmitting || isSyncing}
          onFileSelect={fileUpload.handleFileSelect}
          onRemoveFile={
            fileUpload.selectedFile
              ? fileUpload.handleRemoveFile
              : handleRemoveExistingFile
          }
        />

        {isSyncing && (
          <div css={cssSyncingBadgeWrapper}>
            <Badge variant="superior">
              <Stack
                direction="row"
                spacing="4px"
                sx={{ alignItems: "center" }}
              >
                <MotifIcon un-i-motif="arrow_sync" />
                <span>{t("productFeed.status.syncing")}</span>
              </Stack>
            </Badge>
          </div>
        )}

        <FeedCheckSummary feed={feed} />

        <div css={cssButtonContainer}>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              !feedName.trim() || !displayFile || isSubmitting || isSyncing
            }
          >
            {t("productFeed.fileUpload.uploadButton")}
          </Button>
        </div>
      </Stack>
    );
  };
